
import { ProfessionalInterface } from './types';
import { getProfessionById } from '@/services/professions/getProfessionsNew';
import i18n from '@/i18n/config';

// Legacy mapping for old profession names to new slugs
const mapOldProfessionToNew = (oldProfession: string): string | null => {
  if (!oldProfession) return null;
  
  // First try to get directly from the new cache
  const prof = getProfessionById(oldProfession);
  if (prof) return prof.slug;
  
  // Fallback mapping for Hebrew names
  const mapping: Record<string, string> = {
    'אינסטלטור': 'plumber',
    'חשמלאי': 'electrician',
    'שיפוצניק': 'handyman',
    'מנעולן': 'locksmith',
    'מיזוג אוויר': 'hvac-installer',
    'גנן': 'gardener',
    'מדביר': 'exterminator',
    'מנקה': 'cleaner',
    'צלם': 'photographer',
    'בנייה': 'contractor',
    'אדריכל': 'architect',
  };
  return mapping[oldProfession] || null;
};

/**
 * Format a phone number for display
 * @param phone The phone number to format
 */
export const formatPhoneNumber = (phone?: string): string => {
  if (!phone) return '';
  
  // If it's just digits, format as XXX-XXXXXXX
  if (/^\d+$/.test(phone) && phone.length >= 9) {
    const prefix = phone.slice(0, 3);
    const number = phone.slice(3);
    return `${prefix}-${number}`;
  }
  
  return phone;
};

/**
 * Gets a professional from data
 * @param data Professional data
 */
export const getProfessionalFromData = (data: any): ProfessionalInterface => {
  if (!data) return null as any;
  
  // Calculate years of experience based on when they started (if available)
  const experienceYears = data.experience_years || 
    (data.year_established ? new Date().getFullYear() - data.year_established : null);

  // Phone numbers are no longer exposed publicly - use secure reveal mechanism
  // const phoneNumber = formatPhoneNumber(data.phone_number || data.phone || '');
  
  // Ensure review count is always populated
  const reviewCount = data.reviews_count || data.review_count || 0;
  
  // Ensure location is always populated with a fallback value (prefer location over city)
  const notSpecified = i18n.t('common.notSpecified');
  const location = data.location || data.city || notSpecified;

  // Ensure image is always populated with a fallback value
  const image = data.image_url || data.image || 'https://via.placeholder.com/150';

  // Ensure specialties is always populated with an array
  const specialties = data.specialties ||
                     (data.specialty ? [data.specialty] : []).filter(Boolean) ||
                     [data.profession].filter(Boolean) ||
                     [notSpecified];

  // Fallback logic for new fields - backwards compatibility
  const mainProfession = data.main_profession ||
                         mapOldProfessionToNew(data.profession || data.specialty) ||
                         data.profession ||
                         data.specialty ||
                         notSpecified;
  const subSpecializations = data.sub_specializations || [];
  
  // Construct the professional object with all required and optional fields
  return {
    id: data.id,
    name: data.name,
    profession: data.specialty || data.profession || notSpecified,
    // New fields with fallback to old field
    main_profession: mainProfession,
    sub_specializations: subSpecializations,
    rating: data.rating || 0,
    reviewCount: reviewCount, // Ensure reviewCount is always populated
    reviews_count: reviewCount,
    location: location, // Ensure location is always populated
    image: image, // Ensure image is always populated
    image_url: image,
    verified: data.verified || data.is_verified || false,
    specialties: specialties, // Ensure specialties is always populated with a non-empty array
    // Phone and email are no longer exposed publicly - use secure reveal mechanism
    phone: '',
    phoneNumber: '',
    phone_number: '',
    email: undefined,
    bio: data.bio || data.about,
    about: data.bio || data.about,
    created_at: data.created_at || new Date().toISOString(),
    city: data.city || data.location || notSpecified,
    specialty: data.specialty || data.profession || notSpecified,
    area: data.area || data.areas,
    areas: data.area || data.areas,
    category: data.category,
    // Additional fields 
    company_name: data.company_name,
    work_hours: data.work_hours || null,
    working_hours: data.working_hours || null,
    certifications: data.certifications || [],
    experience_years: experienceYears,
    is_verified: data.verified || data.is_verified || false,
    languages: data.languages || [],
    // Social media links
    tiktok_url: data.tiktok_url || null,
    facebook_url: data.facebook_url || null,
    instagram_url: data.instagram_url || null,
    // NEW: UUID-based profession fields from professionals_public_view
    profession_ids: data.profession_ids || [],
    specialization_ids: data.specialization_ids || [],
    // Work cities
    work_cities: data.work_cities || []
  };
};
